import { ClassifierData } from 'types/classifiers';
import { IdentityAddress, IdentityFields } from 'types/clientIdentification';
import { getValidOptionsFromClassifier } from 'utils/formUtil';

export type Option = { label: string; value: string };

export interface AddressFormValues {
    address1: string;
    address2: string;
    zip: string;
    city: string;
    country: Option | null;
}

export interface IdentityFormValues {
    firstName: string;
    lastName: string;
    officialName: string;
    legalForm: string;
    personalCode: string;
    regCode: string;
    dateOfBirth: string | null;
    placeOfBirthCountry: Option | null;
    placeOfBirthCity: string;
    nationalities: Option[];
    identificationAddress: AddressFormValues;
    visitingAddress: AddressFormValues;
    identityDocumentNumber: string;
    identityDocumentCountry: Option | null;
    identityDocumentExpiry: string | null;
    occupation: string;
    employmentStatus: Option | null;
    natureOfBusiness: string;
    registrationDate: string | null;
    registrationAuthority: string;
}

/** The live values of a code list, and the option of a stored code the list no longer offers. */
export function classifierOptions(classifier: ClassifierData | undefined) {
    const options = getValidOptionsFromClassifier<string, string>(classifier);
    const optionOf = (code: string | null): Option | null =>
        code ? (options.find((option) => option.value === code) ?? { label: code, value: code }) : null;
    return { options, optionOf };
}

const text = (value: string | null | undefined) => value ?? '';
const stored = (value: string) => value.trim() || null;

function addressToForm(address: IdentityAddress, countryOf: (code: string | null) => Option | null): AddressFormValues {
    return {
        address1: text(address.address1),
        address2: text(address.address2),
        zip: text(address.zip),
        city: text(address.city),
        country: countryOf(address.countryCode),
    };
}

function addressToFields(form: AddressFormValues): IdentityAddress {
    return {
        address1: stored(form.address1),
        address2: stored(form.address2),
        zip: stored(form.zip),
        city: stored(form.city),
        countryCode: form.country?.value ?? null,
    };
}

export function identityFieldsToForm(
    fields: IdentityFields,
    countryOf: (code: string | null) => Option | null,
    employmentStatusOf: (code: string | null) => Option | null
): IdentityFormValues {
    return {
        firstName: text(fields.firstName),
        lastName: text(fields.lastName),
        officialName: text(fields.officialName ?? fields.name),
        legalForm: text(fields.legalForm),
        personalCode: text(fields.personalCode),
        regCode: text(fields.regCode),
        dateOfBirth: fields.dateOfBirth,
        placeOfBirthCountry: countryOf(fields.placeOfBirthCountryCode),
        placeOfBirthCity: text(fields.placeOfBirthCity),
        nationalities: fields.nationalities.map(countryOf).filter((option): option is Option => !!option),
        identificationAddress: addressToForm(fields.identificationAddress, countryOf),
        visitingAddress: addressToForm(fields.visitingAddress, countryOf),
        identityDocumentNumber: text(fields.identityDocument.number),
        identityDocumentCountry: countryOf(fields.identityDocument.issuingCountryCode),
        identityDocumentExpiry: fields.identityDocument.expiryDate,
        occupation: text(fields.occupation),
        employmentStatus: employmentStatusOf(fields.employmentStatusCode),
        natureOfBusiness: text(fields.natureOfBusiness),
        registrationDate: fields.registrationDate,
        registrationAuthority: text(fields.registrationAuthority),
    };
}

/** The whole shape goes back, the keys the popup did not edit as they were stored. */
export function identityFormToFields(form: IdentityFormValues, before: IdentityFields): IdentityFields {
    const officialName = stored(form.officialName);
    return {
        ...before,
        firstName: stored(form.firstName),
        lastName: stored(form.lastName),
        officialName,
        // The field shows the name where no official name is stored, so clearing it clears that name too.
        name: officialName === null ? null : before.name,
        legalForm: stored(form.legalForm),
        personalCode: stored(form.personalCode),
        regCode: stored(form.regCode),
        dateOfBirth: form.dateOfBirth || null,
        placeOfBirthCountryCode: form.placeOfBirthCountry?.value ?? null,
        placeOfBirthCity: stored(form.placeOfBirthCity),
        nationalities: form.nationalities.map((option) => option.value),
        identificationAddress: addressToFields(form.identificationAddress),
        visitingAddress: addressToFields(form.visitingAddress),
        identityDocument: {
            number: stored(form.identityDocumentNumber),
            issuingCountryCode: form.identityDocumentCountry?.value ?? null,
            expiryDate: form.identityDocumentExpiry || null,
        },
        occupation: stored(form.occupation),
        employmentStatusCode: form.employmentStatus?.value ?? null,
        natureOfBusiness: stored(form.natureOfBusiness),
        registrationDate: form.registrationDate || null,
        registrationAuthority: stored(form.registrationAuthority),
    };
}
